import { useEffect, useMemo, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import type { ColumnDef } from "@tanstack/react-table";
import { formatDistanceToNowStrict, parseISO } from "date-fns";
import { DataTable } from "~/components/data-table";
import { FilterTabs, type FilterTab } from "~/components/filter-tabs";
import { MasterDetail } from "~/components/master-detail";
import { ParcelDetailPanel } from "~/components/ops/parcel-detail-panel";
import { useOpsUnread } from "~/components/ops/unread";
import { PageHeader } from "~/components/page-header";
import { StatusBadge } from "~/components/status-badge";
import { Button } from "~/components/ui/button";
import { api } from "~/lib/api";
import { formatDate, statusLabel } from "~/lib/format";
import { TASK_FILTER_LABEL, TASK_LABEL, TASK_ORDER, workItems, type TaskKey, type WorkItem } from "~/lib/ops-work";
import { cn } from "~/lib/utils";

export const Route = createFileRoute("/_authed/ops/")({
  validateSearch: (search: Record<string, unknown>): { parcel?: string } => ({
    parcel: typeof search.parcel === "string" ? search.parcel : undefined,
  }),
  component: Today,
});

const PIPELINE = ["REGISTERED", "RECEIVED_ABROAD", "RECEIVED_KENYA", "ON_HOLD", "PAID", "OUT_FOR_DELIVERY"];
const URGENT: TaskKey[] = ["reply", "hold"];

type Filter = TaskKey | "all";

function waited(iso?: string | null): string {
  if (!iso) return "";
  const date = parseISO(iso);
  return Number.isNaN(date.getTime()) ? "" : formatDistanceToNowStrict(date);
}

const columns: ColumnDef<WorkItem, unknown>[] = [
  {
    id: "task",
    header: "Do next",
    cell: ({ row }) => {
      const { task } = row.original;
      return (
        <div className="flex min-w-0 items-start gap-2 leading-tight">
          <span className={cn("mt-1.5 size-1.5 shrink-0 rounded-full", URGENT.includes(task.key) ? "bg-warning" : "bg-foreground/30")} />
          <div className="min-w-0">
            <p className="truncate text-[13px] font-medium">{TASK_LABEL[task.key]}</p>
            <p className="text-text-faint truncate text-[11px]">{task.detail}</p>
          </div>
        </div>
      );
    },
    meta: { headClassName: "md:min-w-[190px]", cellClassName: "max-md:max-w-[190px]" },
  },
  {
    id: "customer",
    header: "Customer",
    cell: ({ row }) => {
      const { parcel } = row.original;
      return (
        <div className="max-w-[110px] min-w-0 leading-tight md:max-w-[170px]">
          <p className="truncate text-[13px]">{parcel.customerName || "No name"}</p>
          <p className="text-text-faint tabular truncate text-[11px]">{parcel.customerPhone}</p>
        </div>
      );
    },
  },
  {
    id: "parcel",
    header: "Parcel",
    cell: ({ row }) => {
      const { parcel } = row.original;
      return (
        <div className="max-w-[190px] min-w-0 leading-tight">
          <p className="truncate text-[13px]">{parcel.description || "Parcel"}</p>
          <p className="text-text-faint truncate text-[11px]" title={parcel.trackingNumber ?? undefined}>
            {parcel.trackingNumber}
          </p>
        </div>
      );
    },
    meta: { headClassName: "hidden @xl:table-cell", cellClassName: "hidden @xl:table-cell" },
  },
  {
    id: "status",
    header: "Status",
    cell: ({ row }) => <StatusBadge status={row.original.parcel.status} />,
    meta: { headClassName: "hidden @3xl:table-cell", cellClassName: "hidden @3xl:table-cell" },
  },
  {
    id: "waiting",
    header: "Waiting",
    cell: ({ row }) => (
      <span className="text-text-faint tabular text-[12px] whitespace-nowrap" title={formatDate(row.original.parcel.updatedAt)}>
        {waited(row.original.parcel.updatedAt)}
      </span>
    ),
    meta: { headClassName: "hidden w-[100px] md:table-cell", cellClassName: "hidden md:table-cell" },
  },
];

function Today() {
  const { parcel: linkedParcel } = Route.useSearch();
  const [filter, setFilter] = useState<Filter>("all");
  const [selected, setSelected] = useState<string | null>(linkedParcel ?? null);

  useEffect(() => {
    if (linkedParcel) setSelected(linkedParcel);
  }, [linkedParcel]);

  const parcels = useQuery({ queryKey: ["ops", "parcels", "counts"], queryFn: () => api.ops.listParcels({}) });
  const unread = useOpsUnread();

  const work = useMemo(() => workItems(parcels.data ?? [], unread.data?.parcel ?? {}), [parcels.data, unread.data]);
  const shown = filter === "all" ? work : work.filter((item) => item.task.key === filter);

  const tabs: FilterTab<Filter>[] = [
    { value: "all", label: "Everything", count: work.length },
    ...TASK_ORDER.filter((key) => work.some((item) => item.task.key === key)).map((key) => ({
      value: key,
      label: TASK_FILTER_LABEL[key],
      count: work.filter((item) => item.task.key === key).length,
    })),
  ];

  const counts: Record<string, number> = {};
  for (const parcel of parcels.data ?? []) counts[parcel.status ?? ""] = (counts[parcel.status ?? ""] ?? 0) + 1;

  return (
    <>
      <PageHeader
        eyebrow="Work to do"
        title="Today"
        caption={
          parcels.isLoading
            ? undefined
            : work.length === 0
              ? "Nothing is waiting on you"
              : `${work.length} ${work.length === 1 ? "parcel needs" : "parcels need"} you`
        }
      />

      <nav aria-label="Parcels by stage" className="border-hairline bg-hairline grid grid-cols-3 gap-px overflow-hidden rounded-lg border md:grid-cols-6">
        {PIPELINE.map((status) => (
          <Link key={status} to="/ops/parcels" search={{ status }} className="bg-card hover:bg-accent flex flex-col gap-1 px-3 py-2.5 transition-colors">
            <span className="tabular text-[20px] leading-none font-semibold tracking-[-0.015em]">{counts[status] ?? 0}</span>
            <span className="text-muted-foreground truncate text-[11.5px]">{statusLabel(status)}</span>
          </Link>
        ))}
      </nav>

      {parcels.isError ? (
        <div className="border-hairline bg-card rounded-lg border px-6 py-8 text-center">
          <p className="text-[14px] font-medium">The work list did not load</p>
          <p className="text-text-faint mt-1 text-[13px]">This is a connection problem, not an empty list.</p>
          <Button variant="outline" className="mt-3" onClick={() => void parcels.refetch()}>
            Try again
          </Button>
        </div>
      ) : (
        <>
          {tabs.length > 2 && (
            <FilterTabs
              label="Filter work"
              value={filter}
              tabs={tabs}
              onChange={(next) => {
                setFilter(next);
                setSelected(null);
              }}
            />
          )}
          <MasterDetail
            open={!!selected}
            onClose={() => setSelected(null)}
            detail={selected ? <ParcelDetailPanel parcelId={selected} /> : null}
            emptyHint="Select a row to act on it"
            title="Parcel"
          >
            <DataTable
              data={shown}
              columns={columns}
              limit={shown.length || 50}
              offset={0}
              onPageChange={() => {}}
              loading={parcels.isLoading}
              getRowId={(item) => item.parcel.id!}
              onRowClick={(item) => setSelected(item.parcel.id!)}
              selectedRowId={selected ?? undefined}
              caption="Parcels that need action, most urgent first"
              emptyTitle="All clear"
              emptyBody="Nothing needs action right now. New work appears here as parcels move."
            />
          </MasterDetail>
        </>
      )}
    </>
  );
}
